import { useCallback, useEffect, useRef, useState } from 'react';

type TabsScrollState = {
  canScrollLeft: boolean;
  canScrollRight: boolean;
};

type TabsScrollDependencies = {
  content?: unknown;
  topRightContent?: unknown;
  tabsAppend?: unknown;
  append?: unknown;
};

const useTabsScroll = ({
  content,
  topRightContent,
  tabsAppend,
  append,
}: TabsScrollDependencies = {}) => {
  const [scrollState, setScrollState] = useState<TabsScrollState>({
    canScrollLeft: false,
    canScrollRight: false,
  });
  const navScrollRef = useRef<HTMLDivElement>(null);

  const getNavElement = useCallback(
    () =>
      navScrollRef.current?.querySelector<HTMLUListElement>('.nav-tabs') ??
      null,
    []
  );

  const updateScrollState = useCallback(() => {
    const nav = getNavElement();
    if (!nav) {
      return;
    }

    const { scrollLeft, scrollWidth, clientWidth } = nav;
    const maxScrollLeft = scrollWidth - clientWidth;
    const isScrollable = scrollWidth > clientWidth + 1;
    const canScrollLeft = isScrollable && scrollLeft > 0;
    const canScrollRight = isScrollable && scrollLeft < maxScrollLeft - 1;

    setScrollState(prevState => {
      if (
        prevState.canScrollLeft === canScrollLeft &&
        prevState.canScrollRight === canScrollRight
      ) {
        return prevState;
      }

      return {
        canScrollLeft,
        canScrollRight,
      };
    });
  }, [getNavElement]);

  const handleScrollButtonClick = useCallback(
    (direction: 'left' | 'right') => {
      const nav = getNavElement();
      if (!nav) {
        return;
      }

      const delta = Math.max(80, Math.floor(nav.clientWidth * 0.6));
      const scrollDelta = direction === 'left' ? -delta : delta;

      if (typeof nav.scrollBy === 'function') {
        nav.scrollBy({ left: scrollDelta, behavior: 'smooth' });
      } else {
        nav.scrollLeft = Math.min(
          nav.scrollWidth - nav.clientWidth,
          Math.max(0, nav.scrollLeft + scrollDelta)
        );
      }
    },
    [getNavElement]
  );

  useEffect(() => {
    updateScrollState();
  }, [updateScrollState, content, topRightContent, tabsAppend, append]);

  useEffect(() => {
    const nav = getNavElement();
    if (!nav) {
      return undefined;
    }

    if (typeof ResizeObserver === 'undefined') {
      if (typeof window === 'undefined') {
        return undefined;
      }

      const handleResize = () => updateScrollState();
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }

    const resizeObserver = new ResizeObserver(() => updateScrollState());
    resizeObserver.observe(nav);

    return () => resizeObserver.disconnect();
  }, [getNavElement, updateScrollState]);

  return {
    handleScrollButtonClick,
    navScrollRef,
    scrollState,
    updateScrollState,
  };
};

export default useTabsScroll;
